import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import moment from "moment";

import Modal from "@cayuse/shared-react.components.modal";
import Button from "@cayuse/shared-react.components.button";
import FormattedDatetime from "@cayuse/shared-react.components.formatted-datetime";
import FormattedRichText from "@cayuse/shared-react.components.formatted-rich-text";
import RichText from "@cayuse/shared-react.components.form.inputs.rich-text";

import { useDictionary } from "hooks";
import { useRichTextAccessibility } from "hooks/accessibility";
import { shellSelectors } from "store/selectors";
import { convertToPlainText, decodeString } from "utils";
import { NOTES_MAX_LENGTH } from "../../globals/constants";

import styles from "./styles.css";

const EditableContent = ({ onUpdateNoteChange, updatedMessage, hasTooLongMessage, richTextFormats }) => {
	const dictionary = useDictionary("common.noteDisplayModal");
	const richTextRef = React.useRef(null);

	useRichTextAccessibility(richTextRef);

	useEffect(() => {
		if (richTextRef?.current) {
			richTextRef.current.querySelector(".ql-editor").setAttribute("tabindex", "0");
		}
	}, [ richTextRef ]);

	return (
		<>
			<div ref={richTextRef}>
				<RichText
					className={styles.richTextInput}
					maxLength={NOTES_MAX_LENGTH.toString()}
					onChange={({ value }) => onUpdateNoteChange(value)}
					placeholder={dictionary.notePlaceholder || "Enter your note text here..."}
					value={decodeString(updatedMessage)}
					formats={richTextFormats}
				/>
			</div>
			{hasTooLongMessage && (
				<span className={styles.validationMessage}>
					{dictionary.lengthError}
				</span>
			)}
		</>
	);
};

const PreviewContent = ({ createdBy, createdOn, message }) => {
	return (
		<>
			<div className={styles.richTextWrapper}>
				<FormattedRichText value={decodeString(message)} />
			</div>
			<div className={`ui input ${styles.topMargin}`}>
				<p>
					<strong>Created By: </strong>{`${createdBy} `}
					<FormattedDatetime date={createdOn} />
				</p>
			</div>
		</>
	);
};

const NoteDisplayModal = ({
	title,
	note,
	noteModal,
	onClose,
	update,
	richTextFormats
}) => {
	const dictionary = useDictionary("common.noteDisplayModal");
	const { canEdit, noteIndex, isVisible } = noteModal;
	const { message, createdByName, createdOn } = note;
	const [ updatedMessage, setUpdatedMessage ] = useState("");

	const { fullName } = useSelector(shellSelectors.user);

	const onUpdateNoteChange = value => {
		setUpdatedMessage(value);
	};

	const handleUpdate = () => {
		update(
			noteIndex,
			{
				...note,
				message: encodeURI(updatedMessage),
				updatedBy: fullName,
				updatedOn: moment().toISOString()
			}
		);
	};

	const decodeAndCheckMessage = (message, check) => {
		const decodedMessage = decodeString(message);
		const plainText = convertToPlainText(decodedMessage);

		return check(plainText);
	};

	const hasUpdatedMessage = () =>
		decodeAndCheckMessage(updatedMessage, (text) => text.length > 0);

	const hasTooLongMessage = () =>
		decodeAndCheckMessage(updatedMessage, (text) => text.length > NOTES_MAX_LENGTH);

	useEffect(() => { setUpdatedMessage(decodeString(message)); }, [ message ]);

	return (
		<Modal open={isVisible} onClose={onClose} className={styles.modalContainer} ariaLabel={title || "Note"}>
			<Modal.Header className="ui header whiteOnBlue">
				<h2>{title || "Note"}</h2>
			</Modal.Header>
			<Modal.Content className={styles.modalContent}>
				{canEdit ? (
					<EditableContent
						onUpdateNoteChange={onUpdateNoteChange}
						updatedMessage={updatedMessage}
						hasTooLongMessage={hasTooLongMessage()}
						richTextFormats={richTextFormats}
					/>
				) : (
					<PreviewContent
						createdBy={createdByName}
						createdOn={createdOn}
						message={message}
					/>
				)}
			</Modal.Content>
			<Modal.Actions>
				<Button className={styles.cancelButton} secondary onClick={onClose}>
					{dictionary.cancelEditNote || "Cancel"}
				</Button>
				{canEdit &&
					<Button onClick={handleUpdate} disabled={!hasUpdatedMessage() || hasTooLongMessage()}>
						{dictionary.saveEditNote || "Save"}
					</Button>
				}
			</Modal.Actions>
		</Modal>
	);
};

NoteDisplayModal.propTypes = {
	title: PropTypes.string,
	note: PropTypes.objectOf(PropTypes.string),
	noteModal: PropTypes.shape({
		canEdit: PropTypes.bool,
		noteIndex: PropTypes.number,
		isVisible: PropTypes.bool
	}),
	onClose: PropTypes.func,
	update: PropTypes.func
};

export default NoteDisplayModal;
